/** Order API DTO 타입 — OpenAPI 스펙 기준 */

// ─── Enum / 공통 ──────────────────────────────────────────────────────────────

/** POST /order, PATCH /order/{id}: orderType */
export type ApiOrderType = 'ONLINE' | 'ORGANIZATION';

/** GET /order/list, PATCH /order/{id}: status enum (API 전송값) */
export type ApiOrderStatus =
    | 'DEMO'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CONFIRMED'
    | 'FAILED'
    | 'CANCELLED';

/** GET /order/list, PATCH /order/{id}: paymentStatus enum (API 전송값) */
export type ApiPaymentStatus = 'UN_INVOICED' | 'INVOICED' | 'PAID' | 'REFUNDED';

/** UI 표시용 주문상태 한글 (매핑은 utils에서 처리) */
export type OrderStatus =
    | '데모/견적'
    | '주문진행'
    | '주문완료'
    | '주문확정'
    | '판매실패'
    | '주문취소'
    | '환불완료';

/** UI 표시용 입금상태 한글 */
export type PaymentStatus =
    | '해당 없음'
    | '청구요청'
    | '청구완료'
    | '입금지연'
    | '입금완료'
    | '환불완료';

// ─── 주문 항목 DTO ─────────────────────────────────────────────────────────────

/** POST/PATCH /order 요청의 items 배열 내 항목 (백엔드 DTO와 동일 필드만) */
export interface OrderItemDto {
    productPriceId: number;
    quantity: number;
    unitPrice: number;
    discountPrice?: number;
}

/** GET /order/{id}/detail 상세 응답 items 배열 내 항목 */
export interface OrderItemDetailDto {
    id: number;
    productPriceId: number;
    productPriceName: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    discountPrice: number | null;
    expiresAt: string | null;
    usedAt: string | null;
    /** 품목 단위 원가(백엔드가 내려주는 경우) */
    costPrice?: number | null;
}

// ─── 주문 생성/수정 요청 DTO ────────────────────────────────────────────────────

/** POST /order */
export interface CreateOrderRequestDto {
    organizationId: number;
    downPayment?: number;
    interimPayment?: number;
    finalPayment?: number;
    dealerOrganizationTitle?: string;
    items: OrderItemDto[];
    totalAmount: number;
    description?: string;
    isLicenseActive?: boolean;
    /** ISO 8601 이용 시작일 */
    startAt?: string;
}

export interface CreateOrderResponseDto {
    orderId: number;
}

/** POST /order/open-class — OpenAPI PurchaseOpenClassRequestDto: classIds 만 필수(금액 필드 없음) */
export interface PurchaseOpenClassRequestDto {
    classIds: number[];
    /** 선택 — 응답 orderName 등 로케일 */
    language?: string;
}

export interface ClassPurchaseResultDto {
    classId: number;
    classUserMappingId: number;
}

export interface PurchaseOpenClassResponseDto {
    orderId: number;
    orderNumber: string;
    orderName: string;
    amount: number;
    /** 결제 통화 (KRW, USD, JPY, EUR 등) */
    currency: string;
    clientKey: string;
    /** 레거시 필드 - 일부 응답에서 사용 */
    classes?: ClassPurchaseResultDto[];
}

/** PATCH /order/{orderId} */
export interface UpdateOrderRequestDto {
    organizationId: number;
    status?: ApiOrderStatus;
    downPayment?: number;
    interimPayment?: number;
    finalPayment?: number;
    dealerOrganizationTitle?: string;
    items: OrderItemDto[];
    totalAmount: number;
    /** 총 원가는 PATCH 본문에 포함 불가(ValidationPipe). 비고(description) 임베드로만 전달 */
    description?: string;
    isLicenseActive?: boolean;
    startAt?: string;
    paymentStatus?: ApiPaymentStatus;
}

export interface UpdateOrderResponseDto {
    message: string;
}

// ─── 주문 목록 DTO ─────────────────────────────────────────────────────────────

/** GET /order/list 응답 items 배열 내 항목 */
export interface OrderListItemDto {
    orderId: number;
    orderNumber: string;
    orderType: ApiOrderType;
    /** 온라인 주문 시 사용자명 (nullable) */
    userName: string | null;
    /** 구매/소속 기관 ID (목록 API가 내려주는 경우 — 임베드 필터에 우선 사용) */
    organizationId?: number | null;
    /** 기관 주문 시 기관명 (GET /order/my, /order/organization 등에서 사용) */
    organizationName?: string | null;
    /** 기관 주문 시 기관명 (GET /order/list 응답 필드명) */
    organizationTitle?: string | null;
    /** 목록 API가 내려주는 경우: 단일 상품명 요약 */
    productName?: string | null;
    /** 목록 API가 내려주는 경우: 포함 상품명 목록 */
    productNames?: string[] | null;
    productTypes: ('CONTENT' | 'EQUIPMENT' | 'CUSTOM_SERVICE')[];
    /** 목록 API가 내려주는 경우(미수신 시 상품명 등에서 추정) */
    currency?: string | null;
    /** 수강·이용 기간(학생 목록 API가 줄 때) */
    startAt?: string | null;
    expiresAt?: string | null;
    /** 최초 사용(플레이) 시각 — 온라인 주문 「주문확정」 표시 판별 */
    usedAt?: string | null;
    /** 백엔드가 주문 행에 내려주는 경우 — 수강기간 표시(일수 vs 참여기간) 분기 */
    isOnlineClass?: boolean | null;
    regularPriceTotal: number;
    discountedPriceTotal: number;
    totalAmount: number;
    status: ApiOrderStatus;
    paymentStatus: ApiPaymentStatus;
    isLicenseActive: boolean;
    createdAt: string;
    /** 구매 항목 목록 (API 문서 기준, GET /order/my에만 포함) */
    purchaseItems?: PurchaseItemDto[];
    /** 토스페이먼츠 영수증 URL (GET /order/my에만 포함, 토스 결제 내역이 없으면 null) */
    receiptUrl?: string | null;
}

export interface GetOrderListRequestDto {
    page: number;
    pageSize: number;
    searchText?: string;
    /** 백엔드가 지원하면 해당 기관 주문만 조회 (클래스 생성 등) */
    organizationId?: number;
    /** 파트너 기관 ID (오픈클래스 주문 필터링) */
    partnerOrganizationId?: number;
    startDate?: string;
    endDate?: string;
    orderType?: ApiOrderType;
    status?: ApiOrderStatus;
    paymentStatus?: ApiPaymentStatus;
    /** GET /order/list 는 쿼리에 language 미지원 — 다국어는 주문 상세·GET /order/my 등 i18n 엔드포인트 */
}

/** GET /order/organization — 기관 스코프는 액세스 토큰으로 결정, 쿼리에 organizationId 없음 */
export interface GetOrderOrganizationRequestDto {
    page: number;
    pageSize: number;
    searchText?: string;
    startDate?: string;
    endDate?: string;
    orderType?: ApiOrderType;
    status?: ApiOrderStatus;
    paymentStatus?: ApiPaymentStatus;
}

export interface GetOrderListResponseDto {
    orders: OrderListItemDto[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/** 구매 항목 (오픈클래스 또는 프로덕트) */
export interface PurchaseItemDto {
    type: 'OPEN_CLASS' | 'PRODUCT';
    id: number;
    title: string;
    price: number;
}

/** GET /order/my 한 행 (OpenAPI OrderListByUserItemDto + 백엔드 확장 필드) */
export interface OrderListByUserItemDto {
    id: number;
    orderNumber: string;
    orderType: ApiOrderType;
    status: ApiOrderStatus;
    /** 목록 API가 내려주면 목록 UI에 반영 */
    paymentStatus?: ApiPaymentStatus | null;
    /** 판매처·제휴 기관명 등 */
    dealerOrganizationTitle: string | null;
    /** 구매자 소속/주문 기관명(백엔드가 별도 필드로 줄 때) */
    organizationName?: string | null;
    userName?: string | null;
    productName?: string | null;
    productNames?: string[] | null;
    productTypes?: ('CONTENT' | 'EQUIPMENT' | 'CUSTOM_SERVICE')[] | null;
    totalAmount: number;
    downPayment: number;
    interimPayment: number;
    finalPayment: number;
    createdAt: string;
    isLicenseActive: boolean;
    startAt?: string | null;
    expiresAt?: string | null;
    usedAt?: string | null;
    /** 클래스가 온라인(비대면)인지 — 목록 API 확장 필드 */
    isOnlineClass?: boolean | null;
    maxStudentCount?: number | null;
    currentStudentCount?: number;
    /** 토스페이먼츠 영수증(매출전표) URL — 토스 결제 내역이 없으면 null */
    receiptUrl: string | null;
    /** 구매 항목 목록 (API 문서 기준) */
    purchaseItems?: PurchaseItemDto[];
}

/** GET /order/my 응답 */
export interface GetOrderListByUserResponseDto {
    orders: OrderListByUserItemDto[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// ─── 주문 상세 DTO ─────────────────────────────────────────────────────────────

/** GET /order/{orderId}/detail */
export interface GetOrderDetailResponseDto {
    orderId: number;
    orderNumber: string;
    /** 일부 백엔드가 루트에 클래스·상품명을 줄 때 (품목 배열과 별도) */
    productTitle?: string | null;
    classTitle?: string | null;
    orderType?: ApiOrderType;
    organizationId: number;
    organizationName: string | null;
    userId: number | null;
    userName: string | null;
    status: ApiOrderStatus | null;
    downPayment: number;
    interimPayment: number;
    finalPayment: number;
    dealerOrganizationTitle: string | null;
    items: OrderItemDetailDto[];
    totalAmount: number;
    /** 등록 시 입력 원가 합 — 없으면 매출정보에서 원가 합계를 계산하지 못할 수 있음 */
    totalCostAmount?: number | null;
    description: string | null;
    isActive: boolean;
    isLicenseActive: boolean;
    startAt: string | null;
    expiresAt: string | null;
    usedAt: string | null;
    /** 목록과 동일 — 상세에 있으면 카드 보조문에 반영 */
    isOnlineClass?: boolean | null;
    paymentStatus: ApiPaymentStatus | null;
    demoAt: string | null;
    inProgressAt: string | null;
    completedAt: string | null;
    confirmedAt: string | null;
    failedAt: string | null;
    cancelledAt: string | null;
    createdAt: string;
    updatedAt: string;
}

// ─── 통계 DTO ─────────────────────────────────────────────────────────────────

/**
 * GET /order/statistics 버킷 한 개
 * @example { "amountKRW": 30250000, "amountUSD": 5000, "count": 10 }
 */
export interface OrderStatisticsDto {
    amountKRW: number;
    amountUSD: number;
    count: number;
    /** 구 백엔드 호환 (단일 amount 만 주는 경우) */
    amount?: number;
}

/**
 * GET /order/statistics 본문
 * @example { "total": { ... }, "online": { ... }, "corporate": { ... } }
 */
export interface GetOrderStatisticsResponseDto {
    total: OrderStatisticsDto;
    online: OrderStatisticsDto;
    corporate: OrderStatisticsDto;
}

// ─── 엑셀 다운로드 ─────────────────────────────────────────────────────────────

/** POST /order/list/export-excel */
export interface ExportOrderListExcelRequestDto {
    searchText?: string;
    startDate?: string;
    endDate?: string;
    orderType?: ApiOrderType;
    status?: ApiOrderStatus;
    paymentStatus?: ApiPaymentStatus;
    organizationId?: number;
    partnerOrganizationId?: number;
    language?: string;
}

export interface ExportOrderListExcelResponseDto {
    downloadUrl: string;
    totalCount: number;
}

// ─── 삭제 응답 ────────────────────────────────────────────────────────────────

/** DELETE /order/{orderId} */
export interface DeleteOrderResponseDto {
    message: string;
}

// ─── 상태 매핑 유틸 (UI 표시용) ────────────────────────────────────────────────

export const API_ORDER_STATUS_LABEL: Record<ApiOrderStatus, string> = {
    DEMO: '데모/견적',
    IN_PROGRESS: '주문진행',
    COMPLETED: '주문완료',
    CONFIRMED: '주문확정',
    FAILED: '판매실패',
    CANCELLED: '주문취소',
};

/** UI 표시용 — API `CONFIRMED`와 무관하게 실제 확정 조건(플레이 이력·사용시작일) */
export interface OrderStatusDisplayContext {
    orderType: ApiOrderType;
    startAt?: string | null;
    usedAt?: string | null;
}

/**
 * 「주문확정」: 온라인은 플레이 이력 1회 이상(usedAt), 기관은 사용시작일 이후
 */
export function isOrderConfirmedForDisplay(
    status: ApiOrderStatus,
    ctx: OrderStatusDisplayContext
): boolean {
    if (status !== 'CONFIRMED') return false;
    if (ctx.orderType === 'ONLINE') {
        return Boolean(typeof ctx.usedAt === 'string' && ctx.usedAt.trim());
    }
    if (ctx.orderType === 'ORGANIZATION') {
        const raw = typeof ctx.startAt === 'string' ? ctx.startAt.trim() : '';
        if (!raw) return false;
        const ymd = raw.slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
        const today = new Date();
        const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return todayYmd >= ymd;
    }
    return false;
}

/** `strictApiConfirmed`: 관리자·마스터 주문 화면 — API `CONFIRMED`를 항상 「주문확정」(학생 목록용 구분 없음) */
export type GetDisplayOrderStatusLabelOptions = {
    strictApiConfirmed?: boolean;
};

/** 목록·상세: 결제 직후 API가 CONFIRMED여도 확정 조건 전이면 「주문완료」(학생 UX, `strictApiConfirmed` 미사용 시) */
export function getDisplayOrderStatusLabel(
    status: ApiOrderStatus,
    ctx: OrderStatusDisplayContext,
    options?: GetDisplayOrderStatusLabelOptions
): string {
    if (
        !options?.strictApiConfirmed &&
        status === 'CONFIRMED' &&
        !isOrderConfirmedForDisplay(status, ctx)
    ) {
        return '주문완료';
    }
    return API_ORDER_STATUS_LABEL[status];
}

export const API_PAYMENT_STATUS_LABEL: Record<ApiPaymentStatus, string> = {
    /** 최초 등록·데모/견적 등 청구 전 — UI에서 null과 동일 취급 */
    UN_INVOICED: '해당없음',
    INVOICED: '청구완료',
    PAID: '입금완료',
    REFUNDED: '환불완료',
};

/** 목록·상세 공통: 입금상태 null·UN_INVOICED는 해당없음으로 표시 */
export function formatApiPaymentStatusForDisplay(
    status: ApiPaymentStatus | null | undefined,
    notApplicableLabel?: string
): string {
    const na = notApplicableLabel ?? API_PAYMENT_STATUS_LABEL.UN_INVOICED;
    if (!status || status === 'UN_INVOICED') return na;
    return API_PAYMENT_STATUS_LABEL[status] ?? status;
}

export const API_ORDER_TYPE_LABEL: Record<ApiOrderType, string> = {
    ONLINE: '온라인 주문',
    ORGANIZATION: '기관 주문',
};

export const API_PRODUCT_TYPE_LABEL: Record<string, string> = {
    CONTENT: '콘텐츠',
    EQUIPMENT: '장비/물품',
    CUSTOM_SERVICE: '커스텀 서비스',
};
